/**
 * Step 3: Vakcompetenties Beoordelen
 *
 * Manager scores task-related (vak) competencies.
 * These competencies are linked to the employee's assigned tasks.
 */

import { useState, useEffect, useMemo } from 'react';
import { useEvaluation, useSaveCompetencyScores } from '../useEvaluations';
import { useEmployeeCompetencies } from '../../employees/useEmployeeCompetencies';
import { useWizard } from './WizardContext';
import { ScoreInput } from './ScoreInput';
import { AgreementTypeSelector, isAgreementTypeRequired, type AgreementWithDescription } from './AgreementTypeSelector';
import type { CompetencyScore } from '../../../lib/evaluationsApi';
import { evaluationsApi } from '../../../lib/evaluationsApi';

interface Step3CompetenciesProps {
  evaluationId: number;
}

interface LocalCompetencyScore {
  competencyId: number;
  score: number | null;
  toelichting: string;
  selectedAgreementTypes: number[];
  agreements: AgreementWithDescription[];
}

export function Step3Competencies({ evaluationId }: Step3CompetenciesProps) {
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  // Epic 4 (Story 4.2, AC #3, AD-4): scoringModel-dispatch — voor
  // core_tasks_v2-beoordelingen de compactere set (via toegewezen
  // kerntaken); geen interactie-wijziging, alleen welke competenties
  // getoond worden verandert.
  const { data: employeeCompetencies, isLoading: isLoadingComps } = useEmployeeCompetencies(
    evaluation?.employeeId || 0,
    evaluation?.scoringModel
  );
  const saveCompetencyScores = useSaveCompetencyScores();
  const { stepData, updateStepData, prevStep, nextStep } = useWizard();

  const [localScores, setLocalScores] = useState<LocalCompetencyScore[]>([]);

  // Get only vak competencies for this employee (linked to their tasks)
  const vakCompetencies = useMemo(() => {
    if (!employeeCompetencies) return [];
    return employeeCompetencies.vak || [];
  }, [employeeCompetencies]);

  // Points calculation for vak competencies (30% of 100 = 30 points)
  const VAK_COMP_WEIGHT = 30; // TODO: Get from settings

  // Initialize local scores from wizard state or evaluation data
  useEffect(() => {
    if (!vakCompetencies.length) return;

    const existingScores = stepData.step3?.vakCompetencyScores || evaluation?.competencyScores || [];
    const existingAgreements = stepData.step3?.vakCompetencyAgreements || {};
    const initialScores: LocalCompetencyScore[] = vakCompetencies.map((comp: { id: number; name: string; beschrijving: string | null }) => {
      const existing = existingScores.find((s: CompetencyScore) => s.competencyId === comp.id);
      const compAgreements = existingAgreements[comp.id] || [];
      return {
        competencyId: comp.id,
        score: existing?.score ?? null,
        toelichting: existing?.toelichting || '',
        selectedAgreementTypes: existing?.selectedAgreementTypes || [],
        agreements: compAgreements,
      };
    });
    setLocalScores(initialScores);
  }, [vakCompetencies, evaluation, stepData.step3?.vakCompetencyScores, stepData.step3?.vakCompetencyAgreements]);

  // Calculate points info
  const pointsInfo = useMemo(() => {
    const compCount = vakCompetencies.length;
    const pointsPerComp = compCount > 0 ? VAK_COMP_WEIGHT / compCount : 0;
    let totalEarned = 0;

    localScores.forEach((ls) => {
      if (ls.score !== null) {
        totalEarned += (ls.score / 5) * pointsPerComp;
      }
    });

    return {
      pointsPerComp: Math.round(pointsPerComp * 100) / 100,
      totalEarned: Math.round(totalEarned * 100) / 100,
      maxPoints: VAK_COMP_WEIGHT,
    };
  }, [localScores, vakCompetencies]);

  const handleScoreChange = (competencyId: number, score: number) => {
    setLocalScores((prev) =>
      prev.map((ls) => {
        if (ls.competencyId === competencyId) {
          // Clear agreement types and agreements if score is >= 3
          const newAgreementTypes = score >= 3 ? [] : ls.selectedAgreementTypes;
          const newAgreements = score >= 3 ? [] : ls.agreements;
          return { ...ls, score, selectedAgreementTypes: newAgreementTypes, agreements: newAgreements };
        }
        return ls;
      })
    );
  };

  const handleToelichtingChange = (competencyId: number, toelichting: string) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.competencyId === competencyId ? { ...ls, toelichting } : ls))
    );
  };

  const handleAgreementTypesChange = (competencyId: number, types: number[]) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.competencyId === competencyId ? { ...ls, selectedAgreementTypes: types } : ls))
    );
  };

  const handleAgreementsChange = (competencyId: number, agreements: AgreementWithDescription[]) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.competencyId === competencyId ? { ...ls, agreements } : ls))
    );
  };

  const handleSave = async () => {
    const validScores: CompetencyScore[] = localScores
      .filter((ls) => ls.score !== null)
      .map((ls) => ({
        competencyId: ls.competencyId,
        score: ls.score!,
        toelichting: ls.toelichting || null,
        selectedAgreementTypes: ls.selectedAgreementTypes.length > 0 ? ls.selectedAgreementTypes : undefined,
      }));

    // Collect agreements per competency for wizard state
    const vakCompetencyAgreements: Record<number, AgreementWithDescription[]> = {};
    localScores.forEach((ls) => {
      if (ls.agreements.length > 0) {
        vakCompetencyAgreements[ls.competencyId] = ls.agreements;
      }
    });

    // Update wizard state - vak competency scores, agreements AND total count in step3
    updateStepData('step3', {
      vakCompetencyScores: validScores,
      vakCompetencyAgreements,
      totalVakCompetencies: localScores.length,
    });

    // Save to server
    if (validScores.length > 0) {
      await saveCompetencyScores.mutateAsync({ evaluationId, scores: validScores });
    }

    // Save agreements to server (for each competency with agreements)
    for (const ls of localScores) {
      if (ls.agreements.length > 0 && ls.score !== null && ls.score <= 2) {
        for (const agreement of ls.agreements) {
          if (agreement.beschrijving.trim()) {
            try {
              await evaluationsApi.createScoreAgreement(evaluationId, {
                agreementTypeId: agreement.agreementTypeId,
                beschrijving: agreement.beschrijving,
              });
            } catch {
              // Ignore errors for now - agreements might already exist
            }
          }
        }
      }
    }
  };

  // Check if all required agreement types are filled
  const hasInvalidAgreementTypes = localScores.some(
    (ls) => ls.score !== null && ls.score <= 2 && ls.selectedAgreementTypes.length === 0
  );

  const handleNext = async () => {
    await handleSave();
    nextStep();
  };

  const isLoading = isLoadingEval || isLoadingComps;
  const completedCount = localScores.filter((ls) => ls.score !== null).length;
  const totalCount = vakCompetencies.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Competenties laden...</span>
      </div>
    );
  }

  const renderCompetencyCard = (
    comp: { id: number; name: string; beschrijving: string | null; linkedTasks?: Array<{ id: number; name: string }> },
    score: LocalCompetencyScore | undefined
  ) => (
    <div
      key={comp.id}
      className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* Competency header - same structure as tasks */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{comp.name}</h3>
            {comp.beschrijving && (
              <p className="mt-1 text-sm text-gray-500">{comp.beschrijving}</p>
            )}
          </div>
          <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
            max {pointsInfo.pointsPerComp.toFixed(1)} punten
          </span>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Manager score input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jouw beoordeling
          </label>
          <ScoreInput
            value={score?.score ?? null}
            onChange={(s) => handleScoreChange(comp.id, s)}
            showLabels
            competencyId={comp.id}
          />
        </div>

        {/* Agreement Type Selector (required for score 1-2) */}
        {score && score.score !== null && (
          <AgreementTypeSelector
            selectedTypes={score.selectedAgreementTypes}
            onChange={(types) => handleAgreementTypesChange(comp.id, types)}
            isRequired={isAgreementTypeRequired(score.score)}
            agreements={score.agreements}
            onAgreementsChange={(agreements) => handleAgreementsChange(comp.id, agreements)}
          />
        )}

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Toelichting (optioneel)
          </label>
          <textarea
            rows={2}
            value={score?.toelichting || ''}
            onChange={(e) => handleToelichtingChange(comp.id, e.target.value)}
            placeholder="Geef een toelichting op je beoordeling..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Stap 3: Vakcompetenties Beoordelen</h2>
        <p className="mt-1 text-sm text-gray-500">
          Beoordeel de vakinhoudelijke competenties die gekoppeld zijn aan de taken van deze medewerker.
        </p>
      </div>

      {/* Progress & Points */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Voortgang</span>
            <span className="text-sm text-gray-500">
              {completedCount} van {totalCount} vakcompetenties
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <span className="text-sm font-medium text-gray-700 block mb-2">
            Vakcompetentiescore ({pointsInfo.maxPoints} punten beschikbaar)
          </span>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-gray-900">
              {pointsInfo.totalEarned.toFixed(1)}
            </span>
            <span className="text-lg text-gray-500">/ {pointsInfo.maxPoints}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {pointsInfo.pointsPerComp.toFixed(1)} punten per competentie
          </p>
        </div>
      </div>

      {/* Vak Competencies */}
      {vakCompetencies.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            Vakcompetenties (gekoppeld aan taken)
          </h3>
          <div className="space-y-4">
            {vakCompetencies.map((comp: { id: number; name: string; beschrijving: string | null; linkedTasks?: Array<{ id: number; name: string }> }) =>
              renderCompetencyCard(comp, localScores.find((ls) => ls.competencyId === comp.id))
            )}
          </div>
        </div>
      )}

      {totalCount === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">
            Er zijn geen vakcompetenties gekoppeld aan de taken van deze medewerker.
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={prevStep}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Vorige
        </button>
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saveCompetencyScores.isPending}
            className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50"
          >
            {saveCompetencyScores.isPending ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button
            onClick={handleNext}
            disabled={hasInvalidAgreementTypes}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={hasInvalidAgreementTypes ? 'Selecteer afspraaktypes voor alle competenties met score 1 of 2' : ''}
          >
            Volgende: Basiscompetenties
          </button>
        </div>
      </div>
    </div>
  );
}
